<template>
  <div class="totp-gate">
    <!-- Loading State -->
    <div v-if="isChecking" class="totp-gate__loading">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="48"
        :width="4"
        aria-label="Checking TOTP enrollment status"
      />
      <p class="totp-gate__loading-text">Checking authentication status...</p>
    </div>

    <!-- TOTP Setup Flow (Not Enrolled) -->
    <TOTPSetup
      v-else-if="!isEnrolled"
      :email="email"
      :on-complete="handleSetupComplete"
      :on-cancel="handleCancel"
    />

    <!-- TOTP Verify Flow (Enrolled) -->
    <TOTPVerify
      v-else
      :email="email"
      :on-complete="handleVerifyComplete"
      :on-back="handleCancel"
    />
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import TOTPSetup from './TOTPSetup.vue'
import TOTPVerify from './TOTPVerify.vue'

/**
 * TOTPGate - Intelligent TOTP orchestrator component
 *
 * This component acts as a gate that:
 * - Checks if user has TOTP enrolled (via mock check for Storybook, API in production)
 * - Shows TOTPSetup if not enrolled
 * - Shows TOTPVerify if enrolled
 * - Handles state transitions between setup and verify modes
 * - Calls onComplete when verification succeeds
 * - Calls onCancel if user backs out
 *
 * SECURITY NOTE: This is a UI orchestrator only. In production:
 * - Enrollment checks should query backend API
 * - Secret generation/storage must be server-side
 * - Code verification must be server-side
 * - Never store TOTP secrets in localStorage/sessionStorage
 *
 * Usage:
 * ```vue
 * <TOTPGate
 *   email="user@example.com"
 *   :on-complete="handleComplete"
 *   :on-cancel="handleCancel"
 *   :force-mode="'setup'" // Optional: Force setup mode for Storybook
 * />
 * ```
 *
 * @component TOTPGate
 */
export default Vue.extend({
  name: 'TOTPGate',

  components: {
    TOTPSetup,
    TOTPVerify
  },

  props: {
    /**
     * User's email address (used for enrollment check and passed to child components)
     */
    email: {
      type: String,
      required: true
    },

    /**
     * Called when TOTP verification is successful
     */
    onComplete: {
      type: Function,
      required: true
    },

    /**
     * Called when user cancels TOTP flow
     */
    onCancel: {
      type: Function,
      required: true
    },

    /**
     * Force a specific mode for Storybook testing
     * - 'setup': Force TOTPSetup display (user not enrolled)
     * - 'verify': Force TOTPVerify display (user enrolled)
     * - undefined/null: Auto-detect enrollment status (production behavior)
     */
    forceMode: {
      type: String as () => 'setup' | 'verify' | undefined,
      default: undefined,
      validator: (value: string) => ['setup', 'verify', undefined].includes(value)
    }
  },

  data() {
    return {
      /** Whether user has TOTP enrolled */
      isEnrolled: false,

      /** Whether we're currently checking enrollment status */
      isChecking: true
    }
  },

  mounted() {
    this.checkEnrollmentStatus()
  },

  watch: {
    /**
     * Re-check enrollment status if email changes
     */
    email() {
      this.checkEnrollmentStatus()
    },

    /**
     * Re-check enrollment status if forceMode changes
     */
    forceMode() {
      this.checkEnrollmentStatus()
    }
  },

  methods: {
    /**
     * Check if user has TOTP enrolled
     *
     * In production: This would call a backend API endpoint like:
     *   GET /api/auth/totp/status
     *   Response: { enrolled: boolean, backupCodesRemaining: number }
     *
     * For component library/Storybook: Uses mock enrollment check
     * - If forceMode prop is set, use that mode
     * - Otherwise, mock check based on email pattern (for demo purposes)
     */
    async checkEnrollmentStatus(): Promise<void> {
      this.isChecking = true

      try {
        // Simulate network delay for realistic UX
        await new Promise(resolve => setTimeout(resolve, 500))

        // If forceMode is set, use it (for Storybook testing)
        if (this.forceMode !== undefined) {
          this.isEnrolled = this.forceMode === 'verify'
          return
        }

        // Mock enrollment check for component library/Storybook
        // In production, replace this with actual API call:
        //
        // const response = await this.$http.get('/api/auth/totp/status')
        // this.isEnrolled = response.data.enrolled
        //
        // For now, use mock logic based on email pattern
        this.isEnrolled = this.mockEnrollmentCheck(this.email)
      } catch (error) {
        console.error('Failed to check TOTP enrollment status:', error)
        // On error, default to not enrolled (safer to allow setup)
        this.isEnrolled = false
      } finally {
        this.isChecking = false
      }
    },

    /**
     * Mock enrollment check for Storybook/testing
     * Returns true if email contains 'enrolled' or 'totp' (case-insensitive)
     *
     * This is ONLY for component library demonstration.
     * NEVER use this logic in production - always check server-side.
     *
     * @param email - User's email address
     * @returns Mock enrollment status
     */
    mockEnrollmentCheck(email: string): boolean {
      const lowerEmail = email.toLowerCase()
      return lowerEmail.includes('enrolled') || lowerEmail.includes('totp')
    },

    /**
     * Handle successful TOTP setup completion
     * After setup, mark as enrolled and proceed to complete the flow
     */
    handleSetupComplete(): void {
      // Mark as enrolled (in production, this would be reflected in backend)
      this.isEnrolled = true

      // Call parent's onComplete handler
      this.onComplete()
    },

    /**
     * Handle successful TOTP verification completion
     * Verification successful, proceed with login/access
     */
    handleVerifyComplete(): void {
      // Call parent's onComplete handler
      this.onComplete()
    },

    /**
     * Handle user cancellation
     * User backed out of TOTP flow, return to previous screen
     */
    handleCancel(): void {
      // Call parent's onCancel handler
      this.onCancel()
    }
  }
})
</script>

<style scoped>
.totp-gate {
  width: 100%;
  max-width: 100%;
}

.totp-gate__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 16px;
}

.totp-gate__loading-text {
  margin: 0;
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  font-weight: 500;
}

/* Dark mode support */
.theme--dark .totp-gate__loading-text {
  color: rgba(255, 255, 255, 0.7);
}
</style>
